import enLanding from '@/locales/en/landing.json';
import deLanding from '@/locales/de/landing.json';
import { LOCALE_PATHS, type AppLanguage } from '@/shared/i18n/localeRoutes';

export const SITE_ORIGIN = 'https://www.forecast-engine.com';

export interface Alternate {
  hreflang: string;
  href: string;
}

export interface SeoDescriptor {
  title: string;
  description: string;
  canonical: string;
  ogUrl: string;
  ogTitle: string;
  ogDescription: string;
  twitterTitle: string;
  twitterDescription: string;
  ogLocale: string;
  alternates: Alternate[];
}

const META = {
  en: enLanding.meta,
  de: deLanding.meta,
} as const;

/** og:locale values per language, in the underscore-region form OG expects. */
const OG_LOCALE: Record<AppLanguage, string> = {
  en: 'en_US',
  de: 'de_DE',
};

/** Absolute URL for a locale's landing page. */
function urlFor(locale: AppLanguage): string {
  return `${SITE_ORIGIN}${LOCALE_PATHS[locale]}`;
}

/**
 * The same three alternates appear on every locale. Google drops hreflang sets
 * that are not reciprocal, so this is shared rather than derived per page.
 *
 * Being module-level (not derived from `locale` inside `landingSeo`) is also
 * what keeps this array reference stable across renders, so it does not
 * churn useSeoHead's effect dependency array on every call — a locale-derived
 * version would re-run that effect on every render.
 */
const ALTERNATES: Alternate[] = [
  { hreflang: 'en', href: urlFor('en') },
  { hreflang: 'de', href: urlFor('de') },
  { hreflang: 'x-default', href: urlFor('en') },
];

export interface Copy {
  title: string;
  description: string;
}

/**
 * Per-field fallback to English. An empty <title> is worse than the wrong
 * language, and a locale file can lose a key without anyone noticing.
 * Exported so the fallback is testable without mocking the JSON imports.
 */
export function pickCopy(
  locale: Partial<Copy> | undefined,
  fallback: Copy,
): Copy {
  return {
    title: locale?.title || fallback.title,
    description: locale?.description || fallback.description,
  };
}

export function landingSeo(locale: AppLanguage): SeoDescriptor {
  const { title, description } = pickCopy(META[locale], META.en);
  const canonical = urlFor(locale);

  return {
    title,
    description,
    canonical,
    ogUrl: canonical,
    ogTitle: title,
    ogDescription: description,
    twitterTitle: title,
    twitterDescription: description,
    ogLocale: OG_LOCALE[locale],
    alternates: ALTERNATES,
  };
}
